import { forwardRef, useImperativeHandle, useState } from 'react';

import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Grid,
  HStack,
  Icon,
  Progress,
  Spinner,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { type CountryCode } from 'libphonenumber-js';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuFileText, LuUploadCloud } from 'react-icons/lu';
import { z } from 'zod';

import { Form, FormField, IconTrashEmpty } from '@/components';
import { CountrySelect } from '@/components/CountrySelect';

import { useGetAccountQuery } from '../auth/useAuthQueries';
import { useUploadFileMutationLegacy } from '../file/useUploadFileMutation';
import { useAccountData } from './AccountDataContext';

const accountInfoSchema = z.object({
  name: z.string().min(1, t('auth:users.data.name.required')),
  email: z.string().email(t('auth:users.data.email.invalid')),
  title: z.string().optional(),
  phone: z.string().optional(),
  birthdayDate: z.date().optional(),
  location: z.string().optional(),
  linkedin: z.string().optional(),
  github: z.string().optional(),
  resumeUrl: z.string().optional(),
});

export type AccountInfoFormFields = z.infer<typeof accountInfoSchema>;

export interface AccountInfoFormRef {
  submit: () => void;
}

export const AccountInfoForm = forwardRef<AccountInfoFormRef>((_props, ref) => {
  const { t } = useTranslation(['common', 'candidate', 'cv', 'components']);
  const { data, updatePersonalInfo } = useAccountData();
  const [country, setCountry] = useState<CountryCode>();
  const [rawFile, setRawFile] = useState<File | null>(null);
  const { data: accountData, refetch: refetchAccountData } =
    useGetAccountQuery();
  const fileUploadMutation = useUploadFileMutationLegacy({
    onSuccess: () => {
      refetchAccountData();
    },
  });

  const accountFullName = `${accountData?.name} ${accountData?.surname}`;

  const getFormDefaultValues = (): AccountInfoFormFields => {
    if (!data?.personalInfo) {
      return {
        name: accountFullName ?? '',
        title: accountData?.role ?? '',
        email: accountData?.email ?? '',
        phone: accountData?.phone ?? '',
        birthdayDate: undefined,
        location: '',
        linkedin: '',
        github: '',
        resumeUrl: '',
      };
    }

    return {
      name: data.personalInfo.fullName ?? accountFullName,
      email: data.personalInfo.email ?? accountData?.email ?? '',
      title: data.personalInfo.title ?? '',
      phone: data.personalInfo.phone ?? '',
      birthdayDate: data.personalInfo.birthdayDate
        ? new Date(data.personalInfo.birthdayDate)
        : undefined,
      location: data.personalInfo.address ?? '',
      linkedin: data.personalInfo.socialMedia?.linkedIn ?? '',
      github: data.personalInfo.socialMedia?.github ?? '',
      resumeUrl: data.personalInfo.cvUrl ?? '',
    };
  };

  const form = useForm<AccountInfoFormFields>({
    mode: 'onBlur',
    resolver: zodResolver(accountInfoSchema),
    values: getFormDefaultValues(),
  });

  useImperativeHandle(ref, () => ({
    submit: () => {
      form.handleSubmit(handleSubmit)();
    },
  }));

  const handleSubmit = async (formData: AccountInfoFormFields) => {
    const transformedData = {
      fullName: formData.name,
      email: formData.email,
      title: formData.title,
      phone: formData.phone,
      birthdayDate: formData.birthdayDate?.toISOString(),
      address: formData.location,
      socialMedia: {
        linkedIn: formData.linkedin,
        github: formData.github,
      },
      cvUrl: formData.resumeUrl,
    };

    await updatePersonalInfo(transformedData);
  };

  const getCustomFileName = (
    file: File | string | null | undefined,
    userFullName: string
  ) => {
    if (!file) return '';

    let fileExtension = '';

    if (file instanceof File) {
      fileExtension = file.name.split('.').pop() ?? '';
    } else if (typeof file === 'string') {
      const urlPath = file.split('/').pop() ?? '';
      fileExtension = urlPath.split('.').pop() ?? '';
    }

    return `${userFullName}.${fileExtension}`;
  };

  const getFileSize = (file: File | string | null | undefined) => {
    if (!file) return '';

    if (file instanceof File) {
      const fileSizeInBytes = file.size;
      const fileSizeInKB = fileSizeInBytes / 1024;
      const fileSizeInMB = fileSizeInBytes / (1024 * 1024);

      if (fileSizeInMB >= 1) {
        return `(${fileSizeInMB.toFixed(2)} MB)`;
      }
      return `(${fileSizeInKB.toFixed(2)} KB)`;
    }

    return '';
  };

  const uploadFile = async (file: File) => {
    if (file?.size && file?.size / (1024 * 1024) > 10) {
      return;
    }

    setRawFile(file);
    try {
      const uploadRes = await fileUploadMutation.mutateAsync({
        access: {
          interviewId: 'account',
          candidateId: 'account',
          questionId: 'resume',
          contentType: file.type,
        },
        file,
      });
      form.setValue('resumeUrl', uploadRes.finalObjectUrl ?? '');
    } catch (error) {
      console.error('File upload failed:', error);
    }
  };

  return (
    <>
      {fileUploadMutation.isPending ? (
        <Center minH={20}>
          <Spinner />
        </Center>
      ) : (
        <Form {...form} onSubmit={handleSubmit}>
          <VStack spacing={6} align="stretch">
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
              <FormField
                size={{ base: 'sm', md: 'md' }}
                control={form.control}
                name="name"
                label={t('common:information.fullName')}
                placeholder={t('common:information.fullName')}
                type="text"
              />
              <FormField
                size={{ base: 'sm', md: 'md' }}
                control={form.control}
                name="title"
                label={t('common:jobTitle')}
                placeholder={t('common:jobTitle')}
                type="text"
              />
              <FormField
                size={{ base: 'sm', md: 'md' }}
                control={form.control}
                name="email"
                label={t('common:information.email')}
                placeholder={t('common:information.email')}
                type="email"
              />
              <Box>
                <Text
                  m={0}
                  display="flex"
                  alignItems="baseline"
                  gap={1.5}
                  fontSize="sm"
                  fontWeight={500}
                  color="gray.700"
                >
                  {t('common:information.mobile')}
                </Text>
                <HStack justifyItems="center" columnGap={1} mt={1}>
                  <Stack minWidth={20} rowGap={0.5}>
                    <CountrySelect
                      value={country}
                      size={{ base: 'sm', md: 'md' }}
                      onCountryChange={setCountry}
                      height={{ base: 9, md: 12 }}
                      borderColor="gray.200"
                      borderRadius={{ base: 8, md: 12 }}
                      borderWidth={1}
                    />
                  </Stack>
                  <FormField
                    size={{ base: 'sm', md: 'md' }}
                    type="phone-number"
                    name="phone"
                    control={form.control}
                    placeholder={t('common:information.mobile')}
                    country={country}
                    withCountryCallingCode={false}
                    international
                    useNationalFormatForDefaultCountryValue
                  />
                </HStack>
              </Box>
              <FormField
                size={{ base: 'sm', md: 'md' }}
                control={form.control}
                name="birthdayDate"
                label={t('common:information.birthDate')}
                type="date"
              />
              <FormField
                size={{ base: 'sm', md: 'md' }}
                control={form.control}
                name="location"
                label={t('common:address')}
                placeholder={t('common:address')}
                type="text"
              />
              <Divider gridColumn={{ base: 'span 1', md: 'span 2' }} />
              <Text
                fontSize="md"
                fontWeight="medium"
                gridColumn={{ base: 'span 1', md: 'span 2' }}
              >
                {t('common:socialMedia')}
              </Text>
              <FormField
                size={{ base: 'sm', md: 'md' }}
                control={form.control}
                name="linkedin"
                label="LinkedIn"
                placeholder="LinkedIn"
                type="text"
              />
              <FormField
                size={{ base: 'sm', md: 'md' }}
                control={form.control}
                name="github"
                label="GitHub"
                placeholder="GitHub"
                type="text"
              />
            </Grid>
          </VStack>
        </Form>
      )}

      {/* CV Section Placement */}
      <Divider gridColumn={{ base: 'span 1', md: 'span 2' }} my={2} />
      <Text
        fontSize="md"
        fontWeight="medium"
        gridColumn={{ base: 'span 1', md: 'span 2' }}
        mb={2}
      >
        CV
      </Text>
      <Stack
        gridColumn={{ base: 'span 1', md: 'span 2' }}
        spacing={2}
        alignItems="center"
        direction={{ base: 'column', md: 'row' }}
      >
        <Stack
          borderRadius={{ base: 8, md: 12 }}
          height={{ base: 8, md: 12 }}
          borderWidth={1}
          width="full"
          padding={2}
          alignItems="center"
          direction="row"
          position="relative"
        >
          <Flex
            display="flex"
            justifyContent="center"
            alignItems="center"
            backgroundColor="brand.50"
            width={{ base: 6, md: 8 }}
            borderColor="brand.100"
            borderWidth="1px"
            height={{ base: 6, md: 10 }}
            borderRadius={{ base: 6, md: 8 }}
          >
            <Icon
              as={LuFileText}
              fontSize={{ base: 16, md: 24 }}
              color="brand.500"
            />
          </Flex>
          <Stack flex={1} justifyContent="space-between" flexDirection="row">
            {fileUploadMutation.isPending ? (
              <HStack flex={1}>
                <Progress
                  colorScheme="brand"
                  borderRadius={3}
                  width="full"
                  size="sm"
                  value={fileUploadMutation.uploadProgress}
                />
                <Text
                  width={{ base: '12%', sm: '7%' }}
                  fontSize={{ base: 'xs', md: 'sm' }}
                  alignSelf="end"
                  textAlign="center"
                >
                  {fileUploadMutation.uploadProgress + '%'}
                </Text>
              </HStack>
            ) : rawFile ? (
              <>
                <Text noOfLines={1}>
                  {(() => {
                    if (rawFile)
                      return rawFile.name + ' ' + getFileSize(rawFile);
                    if (form.watch('resumeUrl')) {
                      const fileName = getCustomFileName(
                        form.watch('resumeUrl'),
                        form.watch('name') ?? 'User'
                      );
                      const fileSize = getFileSize(form.watch('resumeUrl'));
                      return fileSize ? fileName + ` (${fileSize})` : fileName;
                    }
                    return '';
                  })()}
                </Text>
                <IconTrashEmpty
                  onClick={() => {
                    setRawFile(null);
                    form.setValue('resumeUrl', '');
                  }}
                  cursor="pointer"
                />
              </>
            ) : (
              <Text>{t('common:actions.upload') + ' CV'}</Text>
            )}
          </Stack>
        </Stack>
        <Stack
          marginTop={{ base: 2, md: 0 }}
          flex={1}
          flexDirection="row"
          justifyContent="flex-start"
          width="full"
          alignItems="center"
        >
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            style={{ display: 'none' }}
            id="cv-upload-input"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) {
                await uploadFile(file);
              }
              e.target.value = '';
            }}
          />
          <Button
            as="label"
            htmlFor="cv-upload-input"
            leftIcon={
              <Icon as={LuUploadCloud} fontSize={20} color="brand.500" />
            }
            variant="outline"
            colorScheme="brand"
            px={4}
            fontWeight={500}
            size={{ base: 'sm', md: 'md' }}
          >
            {t('common:actions.upload')}
          </Button>
          {form.watch('resumeUrl') && (
            <Button
              as="a"
              px={4}
              size={{ base: 'sm', md: 'md' }}
              href={form.watch('resumeUrl')}
              target="_blank"
              variant="link"
              color="brand.500"
              fontWeight={500}
            >
              {t('common:actions.download', "CV'ni indir")}
            </Button>
          )}
        </Stack>
      </Stack>
    </>
  );
});

AccountInfoForm.displayName = 'AccountInfoForm';
